"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { trackBetLinkClick } from "@/utils/analytics";
import {
  type MarketOpportunity,
  type PeerRow,
  type Side,
  getBookDisplayName,
  getClassicBookAvailableLiquidity,
  getClassicShortSideLabel,
  getWhaleWatchTrackingProps,
  hasPlaySideEdge,
  isPlaySideCellLit,
  normalizeWhaleWatchBookKey,
} from "@/lib/whale-watch/whale-watch-shared";
import { OddsHistoryTrigger } from "@/components/odds/odds-history-trigger";
import { ToolSectionHeader } from "@/components/tools/edge-bars";
import {
  WhaleWatchBookLogo as BookLogo,
  type WhaleWatchBookLogoSize,
} from "@/components/whale-watch/whale-watch-book-logo";
import { PeerOddsCell, PeerOddsCellMuted } from "./peer-odds-cell";

type Variant = "desktop" | "mobile";

const LAYOUT: Record<
  Variant,
  {
    cols: string;
    cols3: string;
    head: string;
    row: string;
    bookPad: string;
    playPad: string;
    otherPad: string;
    logo: WhaleWatchBookLogoSize;
  }
> = {
  desktop: {
    cols: "grid-cols-[1fr_88px_64px] @[400px]:grid-cols-[1fr_132px_108px]",
    cols3: "grid-cols-[1fr_88px_64px_64px] @[520px]:grid-cols-[1fr_132px_108px_108px]",
    head: "pt-4 pb-2.5",
    row: "h-[46px]",
    bookPad: "pl-4 gap-2.5",
    playPad: "pr-4",
    otherPad: "pr-4",
    logo: "md",
  },
  mobile: {
    cols: "grid-cols-[1fr_88px_64px]",
    cols3: "grid-cols-[1fr_88px_64px_64px]",
    head: "pt-5 pb-2.5",
    row: "h-[50px]",
    bookPad: "pl-3.5 gap-2",
    playPad: "pr-3",
    otherPad: "pr-3.5",
    logo: "sm",
  },
};

export function WhereToBetTable({
  opp,
  side1,
  side2,
  thirdSide,
  marketName,
  peerRows,
  fairProbFor,
  selectedBookKeys,
  variant,
  openRowHistory,
  emptyState,
}: {
  opp: MarketOpportunity;
  side1: Side;
  side2: Side;
  thirdSide?: Side;
  marketName: string;
  peerRows: PeerRow[];
  fairProbFor: (book: string) => number;
  selectedBookKeys: string[];
  variant: Variant;
  openRowHistory: (row: PeerRow, isBestPrice: boolean) => (() => void) | undefined;
  emptyState: React.ReactNode;
}) {
  const layout = LAYOUT[variant];
  const bestBookKey = normalizeWhaleWatchBookKey(side1.best_book);
  const cols = thirdSide ? layout.cols3 : layout.cols;
  const otherHeader = "text-right text-[11px] font-medium leading-[1.4] text-zinc-500";

  return (
    <div className="@container overflow-hidden rounded-[10px] border border-white/[0.07] bg-white/[0.02]">
      <div className={cn("grid items-end", layout.head, cols)}>
        <ToolSectionHeader
          label="Where To Bet"
          className={cn("flex items-end", layout.bookPad)}
          labelClassName="text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500"
        />
        <div className={cn("text-right", layout.playPad)}>
          <div className="text-[11px] font-medium leading-[1.4] text-accent-value">
            {getClassicShortSideLabel(side1, marketName)}
          </div>
          <div className="font-mono text-[10px] leading-[1.4] text-zinc-500">play side</div>
        </div>
        <div className={cn(otherHeader, layout.otherPad)}>{getClassicShortSideLabel(side2, marketName)}</div>
        {thirdSide && (
          <div className={cn(otherHeader, layout.otherPad)}>
            {getClassicShortSideLabel(thirdSide, marketName)}
          </div>
        )}
      </div>

      {peerRows.map((row) => {
        const isBestPrice = row.side1Price?.odds === side1.best_odds;
        const isPlayBook = normalizeWhaleWatchBookKey(row.book) === bestBookKey;
        const fairProb = fairProbFor(row.book);
        const isLit = isPlaySideCellLit(row, fairProb, selectedBookKeys);
        const rowHistory = openRowHistory(row, isBestPrice);
        const track = (odds: number | undefined, source: "peer_grid" | "peer_grid_muted") => () => {
          trackBetLinkClick(
            getWhaleWatchTrackingProps(opp, {
              sportsbook: row.book,
              odds_american: odds,
              click_source: source,
            }),
          );
        };

        return (
          <div
            key={row.book}
            className={cn(
              "group/history grid items-stretch border-t border-white/[0.05] transition-colors hover:bg-white/[0.03]",
              layout.row,
              cols,
            )}
          >
            <div className={cn("flex min-w-0 items-center pr-2", layout.bookPad)}>
              <BookLogo book={row.book} size={layout.logo} />
              <span className="truncate text-[13px] text-zinc-400">{getBookDisplayName(row.book)}</span>
              {rowHistory && (
                <OddsHistoryTrigger
                  mode="reveal"
                  bookLabel={getBookDisplayName(row.book)}
                  onOpen={rowHistory}
                  className="ml-auto shrink-0"
                />
              )}
            </div>

            <div
              className={cn(
                "flex items-center justify-end",
                layout.playPad,
                isLit && "bg-accent-value/[0.06]",
              )}
            >
              <PeerOddsCell
                odds={row.side1Price?.odds}
                liquidity={getClassicBookAvailableLiquidity(side1, row.book)}
                betLink={row.side1BetLink}
                hasEdge={hasPlaySideEdge(row, fairProb)}
                isPlayBook={isPlayBook}
                variant={variant}
                onBetLinkClick={track(row.side1Price?.odds, "peer_grid")}
              />
            </div>

            <div className={cn("flex items-center justify-end", layout.otherPad)}>
              <PeerOddsCellMuted
                odds={row.side2Odds}
                betLink={row.side2BetLink}
                variant={variant}
                onBetLinkClick={track(row.side2Odds, "peer_grid_muted")}
              />
            </div>

            {thirdSide && (
              <div className={cn("flex items-center justify-end", layout.otherPad)}>
                <PeerOddsCellMuted
                  odds={row.side3Odds}
                  betLink={row.side3BetLink}
                  variant={variant}
                  onBetLinkClick={track(row.side3Odds, "peer_grid_muted")}
                />
              </div>
            )}
          </div>
        );
      })}

      {peerRows.length > 0 ? (
        <div className="border-t border-accent-value/25" />
      ) : (
        <div className="border-t border-white/[0.05]">{emptyState}</div>
      )}
    </div>
  );
}
